<template>
    <!--
      Mini player (HAP-4052 redesign).

      Raised by `store.nowPlaying` when you navigate away from something that is
      playing, and mounted globally in App.vue at
      `fixed bottom-[var(--app-tray-h)]` so it stacks on the app tray rather than
      under it.

      Two things were broken, both mobile-only, and both from the same line —
      the header was `hidden sm:block`:

        1. On a phone the panel showed a transport and nothing else. No title, no
           speaker, no link back. You could hear a sermon with no way to find out
           which one it was.
        2. The close button lived inside that header, and the mobile replacement
           had been commented out. There was NO way to dismiss the mini player on
           a phone short of reloading the page.

      The header is now unconditional and the row is laid out around a thumbnail,
      which also gives the panel something to be about at a glance.
    -->
    <div
        v-if="src"
        class="w-full bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.25)] md:m-4 md:w-auto md:max-w-md md:rounded-2xl md:border md:shadow-card z-50"
    >
        <div class="px-4 pt-3 md:px-5 md:pt-4 flex items-start gap-3">
            <!-- `component :is` rather than two duplicated blocks: an
                 unlinkable collection still shows its artwork and title, it just
                 renders as a plain element instead of a dead link. -->
            <component
                :is="link ? 'router-link' : 'div'"
                v-if="artwork"
                :to="link || undefined"
                class="shrink-0 w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-800 bg-cover bg-center"
                :style="`background-image: url('${artwork}')`"
                :aria-label="message.title"
            />
            <div class="min-w-0 flex-1">
                <div class="text-2xs font-semibold uppercase tracking-[0.2em] text-ncfic-blue-800 dark:text-ncfic-blue-300">
                    Now playing
                </div>
                <component
                    :is="link ? 'router-link' : 'div'"
                    class="mt-0.5 block truncate text-sm md:text-base font-semibold text-gray-800 dark:text-gray-200"
                    :class="link ? 'hover:underline underline-offset-4' : ''"
                    :to="link || undefined"
                >
                    {{ message.title }}
                </component>
                <div v-if="speakerNames" class="truncate text-xs text-gray-500 dark:text-gray-500">{{ speakerNames }}</div>
            </div>
            <button
                @click="store.nowPlaying = {}"
                v-press
                title="Stop and close"
                aria-label="Stop and close the player"
                class="shrink-0 -mr-1 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
                <i class="fal fa-xmark" aria-hidden="true" />
            </button>
        </div>

        <Audio :src="src" :key="src" ref="audio" class="px-4 pb-3 md:px-5 md:pb-4 w-full" />

    </div>
</template>

<script>
import Audio from "./mini-player-audio.vue";
import { audioSrcFor } from '../../helpers/audioSrc';

export default {
    inject: ['store'],
    components: {Audio},
    computed: {
        message() {
            return this.store.nowPlaying
        },
        // Must be the SAME string the page player used, or the saved playback
        // position doesn't transfer — see helpers/audioSrc.js. This used to be
        // the raw S3 URL, so every handoff restarted the track from 0:00.
        src() {
            return audioSrcFor(this.message)
        },
        // `nowPlaying` is raised from 13 places across five collections —
        // resources, sermons, podcast episodes, _sbo blog posts and marketplace
        // listings — so neither the old `/${message.collection}/${id}` (a
        // literal `/undefined/:id` whenever the raise site used a GraphQL
        // projection that omits `collection`) nor a blanket `'resources'`
        // fallback is right: both send most of those to a 404.
        //
        // Only the collections that actually have a detail route get a link.
        // A podcast episode's route is nested under its podcast (`/podcasts/
        // :podcastId/:id`), so it needs the parent id and is only linkable when
        // the record carries one.
        link() {
            const message = this.message
            if (!message?.id) return null
            const collection = message.collection
            if (collection === 'sermons') return `/sermons/${message.id}`
            if (collection === 'podcastEpisodes') {
                const podcastId = message.podcast?.id || message.podcast
                return podcastId ? `/podcasts/${podcastId}/${message.id}` : null
            }
            // Resources are the default: every raise site that omits
            // `collection` today is a resource card or the resource page.
            if (!collection || collection === 'resources') return `/resources/${message.id}`
            return null
        },
        artwork() {
            return this.message?.image?.url || this.message?.video?.thumbnail || null
        },
        speakerNames() {
            const people = this.message?.speakers || this.message?.authors
            if (!Array.isArray(people) || !people.length) return ''
            return people.map(s => s?.title).filter(Boolean).join(', ')
        }
    }
}
</script>
